import React, { useContext, useEffect, useRef } from 'react'
import { useVerificationDataQuery, VerificationDataQuery } from '../../graphql/generated/graphql'
import { Feature, useFeatureFlag } from '../../context/useFeatureFlag'

export enum VerificationStatus {
    Loading = 'Loading',
    NotStarted = 'NotStarted',
    Active = 'Active',
    UserCompleted = 'UserCompleted',
    NeedsSSN = 'NeedsSSN',
    Failed = 'Failed',
    Verified = 'Verified',
}

export enum BillPayStatus {
    NotStarted = 'NotStarted',

    Loading = 'Loading',

    Waiting = 'Waiting',

    SetupError = 'SetupError',

    NeedsSSN4 = 'NeedsSSN4',

    NeedsSSN9 = 'NeedsSSN9',
    Failed = 'Failed',
    Verified = 'Verified',
}

interface IVerificationContext {
    verificationStatus: VerificationStatus
    billPayStatus: BillPayStatus
    isKYCEnabled: boolean
    loading: boolean
    completedAt: Date | null
}

const VerificationContext = React.createContext<IVerificationContext>({
    verificationStatus: VerificationStatus.NotStarted,
    billPayStatus: BillPayStatus.NotStarted,
    isKYCEnabled: false,
    loading: true,
    completedAt: null,
})

export const useVerificationContext = () => {
    const context = useContext(VerificationContext)
    return context
}

interface ComputeVerificationStatusProps {
    loading: boolean
    data: VerificationDataQuery | undefined
}

const computeVerificationStatus = ({
    loading,
    data,
}: ComputeVerificationStatusProps): VerificationStatus => {
    const status = data?.me?.kycStatus?.cognitoKycFlow?.status
    const userCompleted = data?.me?.kycStatus?.cognitoKycFlow?.userCompleted

    if (loading) return VerificationStatus.Loading
    if (!status) return VerificationStatus.NotStarted
    if (status === 'active' && userCompleted) return VerificationStatus.UserCompleted
    if (status === 'active') return VerificationStatus.Active
    if (status === 'success') return VerificationStatus.Verified
    if (status === 'failed') return VerificationStatus.Failed

    return VerificationStatus.NotStarted
}

const computeBillPayStatus = ({ loading, data }: ComputeVerificationStatusProps): BillPayStatus => {
    const entity = data?.me?.methodFiEntity

    if (loading) return BillPayStatus.Loading
    if (!entity) return BillPayStatus.NotStarted

    const status = entity.status
    const hasAddress = !!entity.address?.zip

    const error = data?.me?.methodFiEntity?.error

    if (error === 'INITIAL_PUSH') return BillPayStatus.SetupError
    if (error === 'SSN4') return BillPayStatus.NeedsSSN9
    if (error === 'SSN9') return BillPayStatus.Failed
    if (hasAddress && status === 'incomplete') return BillPayStatus.NeedsSSN4
    if (!hasAddress && status === 'incomplete') return BillPayStatus.Waiting
    if (status === 'active') return BillPayStatus.Verified

    return BillPayStatus.NotStarted
}

export const VerificationProvider: React.FC = ({ children }) => {
    const isKYCEnabled = useFeatureFlag(Feature.KYC)
    const { data, loading, startPolling, stopPolling } = useVerificationDataQuery()

    const isPolling = useRef(false)

    const verificationStatus = computeVerificationStatus({ loading, data })
    const billPayStatus = computeBillPayStatus({ loading, data })
    const completedAtStr = data?.me?.kycStatus?.cognitoKycFlow?.completed_at
    const completedAt = completedAtStr ? new Date(completedAtStr) : null

    /**
     * Poll until cognito update reaches methodfi
     */
    useEffect(() => {
        if (verificationStatus === VerificationStatus.UserCompleted) {
            isPolling.current = true
            startPolling(1000)
            setTimeout(() => {
                stopPolling()
            }, 20000)
        } else if (
            isPolling.current &&
            ![BillPayStatus.Waiting, BillPayStatus.NotStarted].includes(billPayStatus)
        ) {
            isPolling.current = false
            stopPolling()
        }
    }, [verificationStatus, isPolling])

    const value = {
        verificationStatus,
        billPayStatus,
        completedAt,
        isKYCEnabled,
        loading,
    }

    return <VerificationContext.Provider value={value}>{children}</VerificationContext.Provider>
}
